import * as React from "react"
import { useState, useRef, useEffect, useCallback } from "react"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { Calendar } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Calendar as CalendarComponent } from "@/components/ui/calendar"
import { format, parse, isValid } from "date-fns"

/**
 * InlineEditableField - Inline editable field with auto-save
 *
 * Supports text, number, and date types.
 * Auto-saves on blur or Enter, cancels on Escape.
 *
 * @param {string} label - Field label
 * @param {string} type - 'text' | 'number' | 'date'
 * @param {any} value - Current value
 * @param {function} onSave - Called when value should be saved (with new value)
 * @param {boolean} disabled - Whether editing is disabled
 * @param {string} suffix - Optional suffix to show after value (e.g., "kg")
 * @param {string} placeholder - Placeholder text when empty
 * @param {string} className - Additional CSS classes
 */
const InlineEditableField = React.forwardRef(({
  label,
  type = 'text',
  value,
  onSave,
  disabled = false,
  suffix = '',
  placeholder = 'Click to edit',
  className,
  ...props
}, ref) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState('')
  const [originalValue, setOriginalValue] = useState(value)
  const inputRef = useRef(null)
  const [datePickerOpen, setDatePickerOpen] = useState(false)

  // Format value for display
  const formatDisplayValue = useCallback((val) => {
    if (val === null || val === undefined || val === '') {
      return null
    }
    if (type === 'date') {
      try {
        const date = new Date(val)
        if (isValid(date)) {
          return format(date, 'd MMM yyyy')
        }
      } catch {
        return val
      }
    }
    if (type === 'number') {
      const num = parseFloat(val)
      if (!isNaN(num)) {
        return Number.isInteger(num) ? num.toString() : num.toFixed(1)
      }
    }
    return val
  }, [type])

  // Update edit value when prop value changes externally
  useEffect(() => {
    if (!isEditing) {
      if (type === 'date' && value) {
        try {
          const date = new Date(value)
          if (isValid(date)) {
            setEditValue(format(date, 'yyyy-MM-dd'))
          }
        } catch {
          setEditValue(value || '')
        }
      } else {
        setEditValue(value?.toString() || '')
      }
      setOriginalValue(value)
    }
  }, [value, isEditing, type])

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current && type !== 'date') {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing, type])

  const handleClick = () => {
    if (!disabled) {
      if (type === 'date') {
        setDatePickerOpen(true)
      } else {
        setIsEditing(true)
      }
    }
  }

  const handleChange = (e) => {
    const newValue = e.target.value

    if (type === 'number') {
      // Allow empty, partial decimals, and valid numbers
      if (newValue === '' || /^-?\d*\.?\d*$/.test(newValue)) {
        setEditValue(newValue)
      }
    } else {
      setEditValue(newValue)
    }
  }

  const handleSave = () => {
    let saveValue = editValue

    if (type === 'number') {
      const numValue = parseFloat(editValue)
      if (isNaN(numValue)) {
        // Revert to original if invalid
        setEditValue(originalValue?.toString() || '')
        setIsEditing(false)
        return
      }
      saveValue = numValue
    }

    setIsEditing(false)

    // Only call onSave if value actually changed
    if (saveValue !== originalValue) {
      onSave?.(saveValue)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setEditValue(originalValue?.toString() || '')
      setIsEditing(false)
    }
  }

  const handleDateSelect = (date) => {
    if (date) {
      const isoDate = format(date, 'yyyy-MM-dd')
      setEditValue(isoDate)
      setDatePickerOpen(false)

      if (isoDate !== originalValue) {
        onSave?.(isoDate)
      }
    }
  }

  const displayValue = formatDisplayValue(value)

  // Date type - uses popover calendar
  if (type === 'date') {
    const dateValue = value ? new Date(value) : undefined

    return (
      <div className={cn("flex flex-col gap-1", className)}>
        {label && (
          <span className="text-xs text-muted-foreground">{label}</span>
        )}
        <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
          <PopoverTrigger asChild disabled={disabled}>
            <button
              type="button"
              className={cn(
                "inline-flex items-center gap-1 text-left font-medium rounded-md px-2 py-1 -mx-2 -my-1 transition-colors",
                disabled
                  ? "text-muted-foreground cursor-not-allowed opacity-60"
                  : "hover:bg-muted/50 cursor-pointer"
              )}
              disabled={disabled}
            >
              {displayValue || (
                <span className="text-muted-foreground italic">{placeholder}</span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="single"
              selected={dateValue}
              onSelect={handleDateSelect}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
    )
  }

  // Text and number types
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      {label && (
        <span className="text-xs text-muted-foreground">{label}</span>
      )}
      {isEditing ? (
        <div className="inline-flex items-center gap-1">
          <input
            ref={inputRef}
            type="text"
            inputMode={type === 'number' ? 'decimal' : 'text'}
            value={editValue}
            onChange={handleChange}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            className="w-20 h-7 px-2 py-1 bg-white rounded-md shadow-sm outline outline-1 outline-neutral-400 text-sm font-medium focus:outline-2 focus:outline-primary"
            {...props}
          />
          {suffix && (
            <span className="text-sm text-muted-foreground">{suffix}</span>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={handleClick}
          disabled={disabled}
          className={cn(
            "inline-flex items-center gap-1 text-left font-medium rounded-md px-2 py-1 -mx-2 -my-1 transition-colors",
            disabled
              ? "text-muted-foreground cursor-not-allowed opacity-60"
              : "hover:bg-muted/50 cursor-pointer"
          )}
        >
          {displayValue !== null ? (
            <>
              <span>{displayValue}</span>
              {suffix && <span className="text-muted-foreground">{suffix}</span>}
            </>
          ) : (
            <span className="text-muted-foreground italic">{placeholder}</span>
          )}
        </button>
      )}
    </div>
  )
})

InlineEditableField.displayName = "InlineEditableField"

export { InlineEditableField }
